import { ViewMedium, Viewport, ViewSmall } from '@shared-ui/viewport-context';
import * as React from 'react';
import LazyLoad from 'react-lazyload';
import { UitkCard, UitkCardContentSection } from 'uitk-react-cards';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage } from 'uitk-react-images';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkSpacing } from 'uitk-react-spacing';
import { CardLink, ImageBadges, PlaceholderCards } from '../..';
import { ActivityDestinationViewSearch } from '../../../../../../common/__generated__/api';
import { equals, getPageId } from '../../../../../../helpers/common-helper';
import { useStores } from '../../../../../../utils/hooks/useStores';
import { FavoriteButton } from '../../../../../common/favorite-button';
import { ActivityCardContentBottom } from './activity-card-content-bottom';
import { ActivityCardContentTop } from './activity-card-content-top';

export interface ActivityTileCardProps {
  activity: ActivityDestinationViewSearch.ActivityInlineFragment;
  loading: boolean;
  index: number;
  className?: string;
  tag?: 'div' | 'article' | 'aside' | 'footer' | 'header' | 'li' | 'ol' | 'section' | 'ul';
  onClick?: (activity: ActivityDestinationViewSearch.ActivityInlineFragment, index: number) => void;
  activityGroup?: string;
}

const ActivityTileCardComponent: React.FC<ActivityTileCardProps> = ({
  activity,
  loading,
  index,
  tag,
  onClick,
  activityGroup
}): JSX.Element => {
  const { activitySearchStore, shortlistStore, searchCriteriaStore } = useStores();
  const badges = activity?.badges;
  const pageId = getPageId(activitySearchStore);
  const regionName = searchCriteriaStore.searchQuery.location;
  const regionId = searchCriteriaStore.searchQuery.rid;

  const renderActivityCard = (): JSX.Element => (
    <>
      <UitkCard data-testid="activity-tile-card" tag={tag}>
        <UitkLayoutFlex justifyContent="space-between" direction="column" className="max-height">
          <UitkLayoutFlexItem>
            <section>
              <UitkLayoutPosition type="relative">
                <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
                  <div data-testid="activity-tile-card-img">
                    <LazyLoad height="100%" offset={[100, 100]} once>
                      <UitkImage alt={activity.image?.description} src={activity.image?.url} />
                    </LazyLoad>
                  </div>
                  <UitkLayoutPosition
                    type="absolute"
                    position={{ top: 'half', right: 'half', zindex: 'layer3' }}
                    cloneElement
                  >
                    <FavoriteButton
                      activityId={activity.id}
                      activityName={activity.name}
                      page={pageId}
                      shortlistStore={shortlistStore}
                      id={`favoriteActivity${activity.id}`}
                      activityGroup={activityGroup}
                    />
                  </UitkLayoutPosition>
                </UitkFigure>
                {badges && <ImageBadges badges={badges} isVerticalLayout={true} />}
              </UitkLayoutPosition>
              <UitkCardContentSection>
                <ActivityCardContentTop activity={activity} />
              </UitkCardContentSection>
            </section>
          </UitkLayoutFlexItem>
          <UitkLayoutFlexItem>
            <Viewport>
              <ViewSmall>
                <UitkCardContentSection className="uitk-card-text-segment">
                  <ActivityCardContentBottom activity={activity} />
                </UitkCardContentSection>
              </ViewSmall>
              <ViewMedium>
                <UitkSpacing margin={{ blockstart: 'three' }}>
                  <UitkCardContentSection>
                    <ActivityCardContentBottom activity={activity} />
                  </UitkCardContentSection>
                </UitkSpacing>
              </ViewMedium>
            </Viewport>
          </UitkLayoutFlexItem>
        </UitkLayoutFlex>

        <CardLink
          activity={activity}
          index={index}
          regionName={regionName}
          regionId={regionId}
          onClick={onClick}
          isRecommendationTile={false}
        />
      </UitkCard>
    </>
  );

  if (loading) {
    return <PlaceholderCards />;
  }

  return renderActivityCard();
};

export const ActivityTileCard = React.memo(ActivityTileCardComponent, (prevProps, nextProps): boolean => {
  let isActivitySame = Boolean(prevProps.activity) && Boolean(nextProps.activity);
  isActivitySame =
    isActivitySame ||
    (prevProps.activity && nextProps.activity && equals(prevProps.activity.id, nextProps.activity.id));

  return !isActivitySame;
});
